import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import apiPaths from 'constants/api-paths';
import { appDb } from 'dexie/app.db';
import { generateURL } from 'helpers/api-path.helper';
import { switchMap, from, catchError, of, map, throwError } from 'rxjs';
import { CacheKeyService } from '../cache-key.service';
import { DBService } from '../db.service';

@Injectable({
  providedIn: 'root',
})
export class ReportVersioningApiService {
  protected dbService = inject(DBService);
  protected cacheKeyService = inject(CacheKeyService);
  protected http = inject(HttpClient);

  getList(cacheKey?: string) {
    return this.http
      .get(
        generateURL(
          apiPaths.reportVersioningList,
          {},
          new HttpParams({ fromObject: { cacheKey } })
        )
      )
      .pipe(
        switchMap((data: any) => {
          if (data.isAllDb) {
            return from(
              appDb.reportVersioning.clear().then(() =>
                appDb.reportVersioning.bulkAdd(
                  data.items.map((item) => ({
                    ...item,
                    isSynced: 1,
                    isDeleted: item.isDeleted ? 1 : 0,
                  }))
                )
              )
            );
          }
          return from(
            appDb.reportVersioning.bulkPut(
              data.items.map((item) => ({
                ...item,
                isSynced: 1,
                isDeleted: item.isDeleted ? 1 : 0,
              }))
            )
          );
        }),
        catchError(() => {
          return of(null);
        })
      );
  }

  create(data) {
    return this.http.post(generateURL(apiPaths.reportVersioning), data).pipe(
      switchMap((res) =>
        this.dbService.getRow(appDb.cacheKeys, 'reportVersioningKey').pipe(
          switchMap((cache) => {
            return this.getList(cache.hash).pipe(map(() => res));
          })
        )
      ),
      switchMap((res) =>
        this.cacheKeyService
          .checkAndUpdateTables('reportVersioningKey')
          .pipe(map(() => res))
      ),
      catchError((err) => throwError(() => err))
    );
  }

  generate(data) {
    return this.http
      .post(generateURL(apiPaths.reportVersioningGenJson), data)
      .pipe(
        switchMap((res) =>
          this.dbService.getRow(appDb.cacheKeys, 'reportVersioningKey').pipe(
            switchMap((cache) => {
              return this.getList(cache.hash).pipe(map(() => res));
            })
          )
        ),
        switchMap((res) =>
          this.cacheKeyService
            .checkAndUpdateTables('reportVersioningKey')
            .pipe(map(() => res))
        ),
        catchError((err) => throwError(() => err))
      );
  }

  update(id, data) {
    return this.http
      .put(generateURL(apiPaths.reportVersioningId, { id }), data)
      .pipe(
        switchMap((res) =>
          this.dbService.getRow(appDb.cacheKeys, 'reportVersioningKey').pipe(
            switchMap((cache) => {
              return this.getList(cache.hash).pipe(map(() => res));
            })
          )
        ),
        switchMap((res) =>
          this.cacheKeyService
            .checkAndUpdateTables('reportVersioningKey')
            .pipe(map(() => res))
        ),
        catchError((err) => throwError(() => err))
      );
  }
  delete(id) {
    return this.http
      .delete(generateURL(apiPaths.reportVersioningId, { id }))
      .pipe(
        switchMap((res) =>
          this.dbService.getRow(appDb.cacheKeys, 'reportVersioningKey').pipe(
            switchMap((cache) => {
              return this.getList(cache.hash).pipe(map(() => res));
            })
          )
        ),
        switchMap((res) =>
          this.cacheKeyService
            .checkAndUpdateTables('reportVersioningKey')
            .pipe(map(() => res))
        ),
        catchError((err) => throwError(() => err))
      );
  }
}
